<template>
    <el-dialog
            :key="session"
            :visible="isVisible"
            @close="hide"
            :title="title"
            :before-close="beforeClose"
            :show-close="true"
    >
        <div class="call-dialog">
            <div class="call-dialog__body">
                <el-row :gutter="20" class="call-participants">
                    <el-col :span="10">
                        <call-part
                                class="call-participants__item"
                                :conversation="conversation"
                                :direction="direction"
                                :role="getRole('caller')"
                        />
                    </el-col>
                    <el-col :span="4">
                        <div class="call-participants__item call-participants__item_center">
                            <div class="call-participants__arrow">
                                <i class="fas fa-chevron-right fa-3x"/>
                            </div>
                        </div>
                    </el-col>
                    <el-col :span="10">
                        <call-part
                                class="call-participants__item"
                                :conversation="conversation"
                                :direction="direction"
                                :role="getRole('callee')"
                        />
                    </el-col>
                </el-row>
            </div>
            <div class="call-dialog__footer">
                <el-tooltip v-if="canAccept" effect="light" placement="top" content="Accept">
                    <button class="btn btn-success btn_call" @click="accept({})">
                        <i class="fas fa-phone-alt"></i>
                    </button>
                </el-tooltip>
                <el-tooltip v-if="canReject" effect="light" placement="top" content="Reject">
                    <button class="btn btn-danger btn_call" @click="reject({})">
                        <i class="fas fa-phone-slash"></i>
                    </button>
                </el-tooltip>
                <el-tooltip v-if="canHangup" effect="light" placement="top" content="Hangup">
                    <button class="btn btn-danger btn_call" @click="hangup({})">
                        <i class="fas fa-phone-slash"></i>
                    </button>
                </el-tooltip>
                <el-tooltip v-if="canIgnore" effect="light" placement="top" content="Ignore">
                    <button class="btn btn-secondary btn_call" @click="ignore({})">
                        <i class="fas fa-eye-slash"></i>
                    </button>
                </el-tooltip>
                <el-tooltip v-if="canSendVoiceMail" effect="light" placement="top" content="Play Voice Message">
                    <button class="btn btn-secondary btn_call" @click="toggleVoiceMessages()">
                        <i class="fas fa-voicemail"></i>
                    </button>
                </el-tooltip>
            </div>
            <div class="call-dialog__collapse" v-show="isFooterVisible">
                <voice-messages
                        v-if="isOutbound"
                        :url="voiceMessagesUrl"
                        @loaded-data="onVoiceMailItemsLoaded"
                        class="voice-message-pick-list p-1 border border-gray-300"
                ></voice-messages>
            </div>
        </div>
    </el-dialog>
</template>

<script>
  import eventBus from '../../event-bus';
  import {mapActions, mapGetters} from 'vuex';
  import {directions, roles} from '../../types/call';
  import CallPart from './call-part';
  import VoiceMessages from './voice-message-list';

  export default {
    components: {
      CallPart,
      VoiceMessages,
    },

    props: {
      voiceMessagesUrl: String,
    },

    data() {
      return {
        isVisible: false,
        conversation: null,
        direction: null,
        duration: 0,
        timerId: null,
        footer: null,
        session: null,
        hasVoiceMessages: false,
      };
    },

    computed: {
      ...mapGetters('voice', [
        'isStatusConnectingIn',
        'canAccept',
        'canHangup',
        'canReject',
        'canIgnore',
        'hasActiveConnection',
      ]),

      /**
       * @return {string}
       */
      directionText() {
        return _.get({
          [directions.in]: 'Incoming',
          [directions.out]: 'Outgoing'
        }, this.direction, '');
      },

      /**
       * @return {string}
       */
      title() {
        return `${this.directionText} Audio Call`.trim();
      },

      /**
       * @return {boolean}
       */
      isInbound() {
        return this.direction === directions.in;
      },

      /**
       * @return {boolean}
       */
      isOutbound() {
        return this.direction === directions.out;
      },

      /**
       * @return {string}
       */
      elapsedTime() {
        return moment(this.duration * 1000)
          .format(this.duration > 3600 ? 'hh:mm:ss' : 'mm:ss');
      },

      /**
       * @return {boolean}
       */
      canSendVoiceMail() {
        return this.isOutbound && this.hasVoiceMessages;
      },

      /**
       * @return {boolean}
       */
      isFooterVisible() {
        return this.footer !== null;
      },
    },

    methods: {
      ...mapActions('voice', [
        'accept',
        'hangup',
        'reject',
        'ignore',
      ]),

      /**
       * @return {undefined}
       */
      hide() {
        this.isVisible = false;
        this.conversation = null;
        this.direction = null;
        this.hasVoiceMessages = false;
        this.footer = null;
      },

      /**
       * @return {undefined}
       */
      show({direction, conversation}) {
        this.conversation = conversation;
        this.direction = direction;
        this.isVisible = true;
      },


      /**
       * @param {function} done
       */
      beforeClose(done) {
        !this.hasActiveConnection && done();
      },

      /**
       * @param {object} conversation
       */
      onInboundCall({conversation}) {
        this.startNewSession();
        this.show({direction: directions.in, conversation});
        this.startCounter();
      },

      /**
       * @param {object} conversation
       */
      onOutboundCall({conversation}) {
        this.startNewSession();
        this.show({direction: directions.out, conversation});
        this.startCounter();
      },

      onCallEnd() {
        this.hide();
        clearInterval(this.timerId);
        this.timerId = null;
      },

      /**
       * @return {undefined}
       */
      startCounter() {
        this.duration = 0;
        this.timerId = setInterval(() => this.duration = this.duration + 1, 1000);
      },

      /**
       * @param {string} name
       * @return {*}
       */
      getRole(name) {
        return roles[name];
      },

      /**
       * @return {undefined}
       */
      toggleVoiceMessages() {
        if (this.footer === 'voice-messages') {
          this.footer = null;
        } else {
          this.footer = 'voice-messages';
        }
      },

      /**
       * @return {undefined}
       */
      startNewSession() {
        this.session = moment().valueOf();
      },

      /**
       * @param {number|null} count
       */
      onVoiceMailItemsLoaded({count}) {
        if (count > 0) {
          this.hasVoiceMessages = true;
        }
      },
    },

    created() {
      eventBus.$on('voice-call-outbound', this.onOutboundCall);
      eventBus.$on('voice-call-inbound', this.onInboundCall);
      eventBus.$on('voice-call-end', this.onCallEnd);

      this.startNewSession()
    },

    beforeDestroy() {
      eventBus.$off('voice-call-outbound', this.onOutboundCall);
      eventBus.$off('voice-call-inbound', this.onInboundCall);
      eventBus.$off('voice-call-end', this.onCallEnd);
    }
  }
</script>
